/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import Skeleton from '@material-ui/lab/Skeleton';
import { toast } from 'react-toastify';
import JobTable from '../JobTable';
import DefaultColumnFilter from '../../react-table/DefaultColumnFilter';
import TableToolbar from '../../react-table/TableToolbar';
import EditDeleteButtons from './EditDeleteButtons';
import CloseButton from './CloseButton';
import ConfirmationDialog from '../ConfirmationDialog';
import model from '../assets/models/model';
import formatOpenJobs from '../functions/formatOpenJobs';
import deleteJob from '../functions/deleteJob';
import Metrics from '../metrics/Metrics';
import metricModel from '../metrics/metricModel';
import useMetrics from '../hooks/useMetrics';
import Charts from '../charts/Charts';
import { IS_OPEN_TRUE } from '../assets/constants/jobConstants';

const SKELETON_ROWS = 5;

const AdminOpenJob = ({
  jobs,
  isLoading,
  onOpen,
  onComplete,
  selectedJobToClose,
  refs,
}) => {
  const [openJobs, setOpenJobs] = useState([]);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState({});
  const openMetricsRef = React.useRef();

  const onDeleteConfirmationClose = useCallback(() => {
    setJobToDelete({});
    setDeleteConfirmationOpen(false);
  }, []);

  const selectedJobToDelete = useCallback((job) => {
    setJobToDelete(job);
    setDeleteConfirmationOpen(true);
  }, []);

  const confirmedDelete = useCallback(
    async (job) => {
      try {
        await deleteJob(job.id);
        toast.success('Deleted job posting successfully.');
      } catch {
        toast.error('Failed to delete job posting.');
      } finally {
        onDeleteConfirmationClose();
        onComplete();
      }
    },
    [onDeleteConfirmationClose, onComplete]
  );

  const defaultColumn = React.useMemo(
    () => ({
      Filter: DefaultColumnFilter, // sets up default column filter input type
    }),
    []
  );

  const defaultHiddenColumns = React.useMemo(() => {
    return [
      model.hourlyRate.id,
      model.shift.id,
      model.schedule.id,
      model.teleworking.id,
      model.usCitizenshipRequired.id,
      model.travel.id,
      model.travelPercentage.id,
      model.educationRequired.id,
      model.majorSpecialization.id,
      model.jobDescription.id,
    ];
  }, []);

  // Initial state of table parameters
  const initialState = React.useMemo(
    () => ({
      pageSize: 25,
      sortBy: [
        {
          id: model?.jobId?.id,
          desc: true,
        },
      ],
      hiddenColumns: defaultHiddenColumns,
    }),
    [defaultHiddenColumns]
  );

  const columns = React.useMemo(() => {
    return [
      {
        id: 'EditDelete',
        disableFilters: true,
        Cell: ({ row: { original } }) => {
          return (
            <EditDeleteButtons
              row={original}
              selectedJobToDelete={selectedJobToDelete}
              onOpen={onOpen}
            />
          );
        },
      },
      {
        Header: model.jobId.name,
        accessor: model.jobId.id,
      },
      {
        Header: model.jobTitle.name,
        accessor: model.jobTitle.id,
        Cell: ({ row }) => (
          <div style={{ maxWidth: 200 }}>{row.original.jobTitle}</div>
        ),
        reportColumnWidth: '20%',
      },
      {
        Header: model.jobLocation.name,
        accessor: model.jobLocation.id,
        Cell: ({ row }) => (
          <div style={{ maxWidth: 200 }}>{row.original.jobLocation}</div>
        ),
        reportColumnWidth: '20%',
      },
      {
        Header: model.minimumClearance.name,
        accessor: model.minimumClearance.id,
      },
      {
        Header: model.polygraphType.name,
        accessor: model.polygraphType.id,
      },
      {
        Header: model.daysOpen.name,
        accessor: model.daysOpen.id,
      },
      {
        id: 'Close',
        disableFilters: true,
        Cell: ({ row: { original } }) => {
          return (
            <CloseButton
              row={original}
              selectedJobToClose={selectedJobToClose}
            />
          );
        },
      },
      {
        id: model.hourlyRate.id,
        accessor: model.hourlyRate.id,
      },
      {
        id: model.shift.id,
        accessor: model.shift.id,
      },
      {
        id: model.schedule.id,
        accessor: model.schedule.id,
      },
      {
        id: model.teleworking.id,
        accessor: model.teleworking.id,
      },
      {
        id: model.usCitizenshipRequired.id,
        accessor: model.usCitizenshipRequired.id,
      },
      {
        id: model.travel.id,
        accessor: model.travel.id,
      },
      {
        id: model.travelPercentage.id,
        accessor: model.travelPercentage.id,
      },
      {
        id: model.educationRequired.id,
        accessor: model.educationRequired.id,
      },
      {
        id: model.majorSpecialization.id,
        accessor: model.majorSpecialization.id,
      },
      {
        id: model.jobDescription.id,
        accessor: model.jobDescription.id,
      },
    ];
  }, [selectedJobToDelete, onOpen, selectedJobToClose]);

  const reportColumns = columns.filter(
    (column) =>
      !defaultHiddenColumns.includes(column.accessor) &&
      typeof column.id === 'undefined'
  );

  const tableData = React.useMemo(
    () =>
      isLoading ? Array(SKELETON_ROWS).fill({}) : formatOpenJobs(openJobs),
    [isLoading, openJobs]
  );

  const tableColumns = React.useMemo(
    () =>
      isLoading
        ? columns.map((column) => ({
            ...column,
            Cell: <Skeleton />,
          }))
        : columns,
    [isLoading, columns]
  );

  const tableHookProps = useTable(
    {
      columns: tableColumns,
      data: tableData,
      defaultColumn,
      initialState,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect
  );

  const {
    rows,
    preGlobalFilteredRows,
    setGlobalFilter,
    state: { selectedRowIds, globalFilter },
  } = tableHookProps;

  const filteredJobs = React.useMemo(
    () => rows.map((item) => item.original),
    [rows]
  );

  const { totalOpen, averageTimeOpen } = useMetrics({ openJobs: filteredJobs });

  const gadgets = [
    {
      id: 'totalOpen',
      metric: totalOpen,
      attributes: metricModel.totalOpen,
    },
    {
      id: 'averageTimeOpen',
      metric: averageTimeOpen,
      attributes: metricModel.averageTimeOpen,
    },
  ];

  useEffect(() => {
    if (jobs) {
      setOpenJobs(jobs.filter((job) => job[model.isOpen.id] === IS_OPEN_TRUE));
    }
  }, [jobs]);

  const { metricsRef, tableDataRef, allDataRef } = refs;

  useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    tableDataRef.current = {
      type: 'open-jobs',
      columns: reportColumns,
      data: rows.map((row) => row.values),
    };
    allDataRef.current = {
      type: 'open-jobs',
      data: filteredJobs,
    };
    metricsRef.current = openMetricsRef.current;
  }, [rows, reportColumns, filteredJobs, tableDataRef, metricsRef, allDataRef]);

  return (
    <>
      <div>
        <div id="open-metrics" ref={openMetricsRef}>
          <Metrics gadgets={gadgets} />
          <Charts openJobs={filteredJobs} />
        </div>
        <TableToolbar
          numSelected={Object.keys(selectedRowIds).length}
          preGlobalFilteredRows={preGlobalFilteredRows}
          setGlobalFilter={setGlobalFilter}
          globalFilter={globalFilter}
        />
        <JobTable
          tableHookProps={tableHookProps}
          tableColumns={tableColumns}
          onOpen={onOpen}
          columnClicksToIgnore={['EditDelete', 'Close']}
        />
        {jobToDelete.jobId ? (
          <ConfirmationDialog
            onClose={onDeleteConfirmationClose}
            open={deleteConfirmationOpen}
            job={jobToDelete}
            confirmedAction={confirmedDelete}
            confirmationAction="Delete"
            confirmationText={
              <>
                Please confirm you want to delete the job posting{' '}
                <b>
                  {jobToDelete.jobId}: {jobToDelete.jobTitle}
                </b>
              </>
            }
            confirmationTitle={<b>Delete Confirmation ({jobToDelete.jobId})</b>}
          />
        ) : null}
      </div>
    </>
  );
};

AdminOpenJob.defaultProps = {
  jobs: [],
  isLoading: true,
};

AdminOpenJob.propTypes = {
  jobs: PropTypes.any,
  isLoading: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  selectedJobToClose: PropTypes.func.isRequired,
};

export default AdminOpenJob;
